/**
 * useTheme — Dark/light mode state.
 *
 * Persists to localStorage and respects system preference.
 * Applies 'dark' class to <html> element for Tailwind dark mode.
 * (v4.8 Spec — light AND dark mode from day one)
 */

import { useState, useEffect, useCallback } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface UseThemeReturn {
  /** Current setting ('light', 'dark', or 'system') */
  readonly theme: Theme;
  /** The resolved theme applied to the page */
  readonly resolvedTheme: 'light' | 'dark';
  /** Toggle between light and dark (skips system) */
  readonly toggle: () => void;
  /** Set a specific theme */
  readonly setTheme: (theme: Theme) => void;
}

const STORAGE_KEY = 'ods-theme';

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function resolveTheme(theme: Theme): 'light' | 'dark' {
  if (theme === 'system') return getSystemTheme();
  return theme;
}

function applyTheme(resolved: 'light' | 'dark'): void {
  const root = document.documentElement;
  if (resolved === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
  // Update meta theme-color for mobile browsers
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) {
    meta.setAttribute('content', resolved === 'dark' ? '#111115' : '#F2F1ED');
  }
}

export function useTheme(): UseThemeReturn {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window === 'undefined') return 'system';
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
    return 'system';
  });

  const resolved = resolveTheme(theme);

  // Apply theme to DOM
  useEffect(() => {
    applyTheme(resolved);
  }, [resolved]);

  // Listen for system theme changes
  useEffect(() => {
    if (theme !== 'system') return;

    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (): void => {
      applyTheme(getSystemTheme());
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  const setTheme = useCallback((newTheme: Theme): void => {
    setThemeState(newTheme);
    localStorage.setItem(STORAGE_KEY, newTheme);
  }, []);

  const toggle = useCallback((): void => {
    const next = resolved === 'light' ? 'dark' : 'light';
    setTheme(next);
  }, [resolved, setTheme]);

  return { theme, resolvedTheme: resolved, toggle, setTheme };
}
